/**
 * Dumont AI API client for Shipeezi "Ask Dumont" integration.
 * Uses client-exchange for auth; all other calls use the returned Dumont JWT.
 * See shipeezi-dumont-ai/docs/client-integration-uniform-chat.md
 */
import { DUMONT_EXTERNAL_API_KEY } from "../constants";

const buildUrl = (baseUrl: string, path: string, query?: Record<string, string>) => {
  const url = new URL(path, baseUrl.replace(/\/$/, "") + "/");
  if (query) {
    Object.entries(query).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  return url.toString();
};

const dumontFetch = async (
  baseUrl: string,
  token: string,
  path: string,
  options: RequestInit = {}
) => {
  const url = buildUrl(baseUrl, path);
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-External-Api-Key": DUMONT_EXTERNAL_API_KEY,
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`Dumont API ${res.status}: ${errBody || res.statusText}`);
  }
  return res;
};

export interface ClientExchangeParams {
  email: string;
  name?: string;
}

export interface ClientExchangeResponse {
  token: string;
  user: { id?: string; email?: string; name?: string };
  defaultAgentId?: string | null;
}

export async function clientExchange(
  baseUrl: string,
  params: ClientExchangeParams
): Promise<ClientExchangeResponse> {
  const url = buildUrl(baseUrl, "/api/auth/client-exchange");
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-External-Api-Key": DUMONT_EXTERNAL_API_KEY,
    },
    body: JSON.stringify(params),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `Client exchange failed: ${res.status}`);
  }
  return data as ClientExchangeResponse;
}

export interface Convo {
  conversationId?: string;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetConvosResponse {
  conversations: Convo[];
  nextCursor?: string | null;
}

export async function getConvos(
  baseUrl: string,
  token: string,
  params?: { limit?: number; cursor?: string }
): Promise<GetConvosResponse> {
  const query: Record<string, string> = {};
  if (params?.limit != null) query.limit = String(params.limit);
  if (params?.cursor) query.cursor = params.cursor;
  const path = "/api/convos" + (Object.keys(query).length ? "?" + new URLSearchParams(query).toString() : "");
  const res = await dumontFetch(baseUrl, token, path);
  return res.json();
}

export async function getConvo(
  baseUrl: string,
  token: string,
  conversationId: string
): Promise<Convo> {
  const res = await dumontFetch(baseUrl, token, `/api/convos/${encodeURIComponent(conversationId)}`);
  return res.json();
}

export interface Message {
  messageId?: string;
  conversationId?: string;
  sender?: string;
  text?: string;
  createdAt?: string;
  isCreatedByUser?: boolean;
  content?: Array<{
    type?: string;
    text?: string;
    think?: string;
  }>;
}

export async function getMessages(
  baseUrl: string,
  token: string,
  conversationId: string
): Promise<Message[]> {
  const res = await dumontFetch(
    baseUrl,
    token,
    `/api/messages/${encodeURIComponent(conversationId)}`
  );
  return res.json();
}

export interface AgentsChatRequest {
  endpoint: string;
  agent_id: string;
  text: string;
  conversationId?: string | null;
  parentMessageId?: string | null;
}

export interface AgentsChatResponse {
  streamId: string;
  conversationId: string;
  status: string;
}

export async function postChat(
  baseUrl: string,
  token: string,
  body: AgentsChatRequest
): Promise<AgentsChatResponse> {
  const res = await dumontFetch(baseUrl, token, "/api/agents/chat", {
    method: "POST",
    body: JSON.stringify({
      ...body,
      conversationId: body.conversationId ?? "new",
    }),
  });
  return res.json();
}

/**
 * Subscribe to chat stream via SSE. Returns an AbortController so the caller can abort.
 * Calls onMessage with parsed SSE data (string or object if JSON). Calls onEnd when stream finishes.
 */
export function subscribeChatStream(
  baseUrl: string,
  token: string,
  streamId: string,
  onMessage: (data: string | object) => void,
  onError?: (err: Error) => void,
  onEnd?: () => void
): AbortController {
  const ac = new AbortController();
  const url = buildUrl(baseUrl, `/api/agents/chat/stream/${encodeURIComponent(streamId)}`);
  fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-External-Api-Key": DUMONT_EXTERNAL_API_KEY,
    },
    signal: ac.signal,
  })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Stream ${res.status}`);
      const reader = res.body?.getReader();
      if (!reader) {
        onEnd?.();
        return;
      }
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]" || data === "") continue;
              try {
                onMessage(JSON.parse(data));
              } catch {
                onMessage(data);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
        onEnd?.();
      }
    })
    .catch((err) => {
      if (err?.name !== "AbortError" && onError) onError(err);
      onEnd?.();
    });
  return ac;
}

export async function getGeneratedTitle(
  baseUrl: string,
  token: string,
  conversationId: string
): Promise<string | null> {
  try {
    const res = await dumontFetch(
      baseUrl,
      token,
      `/api/convos/gen_title/${encodeURIComponent(conversationId)}`
    );
    const data = await res.json();
    return data?.title ?? null;
  } catch {
    return null;
  }
}

export interface AgentItem {
  id?: string;
  name?: string;
}

export async function getAgents(baseUrl: string, token: string): Promise<AgentItem[]> {
  const res = await dumontFetch(baseUrl, token, "/api/agents");
  const data = await res.json();
  const list = data?.data ?? data?.agents ?? [];
  return Array.isArray(list) ? list : [];
}